import React, { FormEvent, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { FileEvent } from '@components/buttons/BrowseFilesButton';
import Button, { ButtonStyle } from '@components/buttons/Button/Button';
import SubmitBtn from '@components/buttons/FormButtons/SubmitBtn';
import { WarningBox } from '@components/cards/messageBox';
import Checkbox from '@components/inputs/Checkbox';
import DropDownMenu from '@components/inputs/DropDownMenu';
import Label from '@components/inputs/text/Label';
import WarningModal from '@components/modal/info/Warning';
import { NavBar } from '@components/navigation/NavBar/NavBar';
import UploadFilePanel from '@components/panels/UploadFile/UploadFilePanel';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import { logger as sentryLogger } from '@sentry/react';
import { CustomerDocumentUploadRequest } from '@services/documentStore/DocumentStore.types';
import DocumentStoreService from '@services/documentStore/DocumentStoreService';
import { EnvTag, getEnvTag } from '@utils/AppEnv';
import { Country } from '@utils/constants/localisation';
import { textBoxStyle } from '@utils/styles/textboxStyle';
import { DocumentDetail, documentDetailInitialState, documentDetailReducer } from '@views/IdentityVerificationDocumentUpload/documentDetailReducer';
import { ALLOWED_FILE_EXTENSIONS, FILE_ACCEPT_TYPES } from '@views/IdentityVerificationDocumentUpload/validateDocumentDetail';
import { HoursDrivenPerWeek, TripsCompleted, UberTenure } from '@views/UberProof/UberProof.types';
import { validateUberProof } from '@views/UberProof/UberProof';

interface FlexiOwnUberProofV2Props {
  navigateTarget: string;
  custId: string;
  country: Country;
  potentialId: string;
}

export default function FlexiOwnUberProofV2({
  navigateTarget,
  custId,
  country,
  potentialId,
}: FlexiOwnUberProofV2Props) {
  const navigate = useNavigate();
  const msalContext = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(false);

  const [uberProofDetails, uberProofDispatch] = useReducer(documentDetailReducer, documentDetailInitialState);

  const navigateToNextStep = () => navigate(navigateTarget);

  const preventScrollIncrement = (event: React.WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur();
  };

  const handleSelectChange = (field: keyof DocumentDetail) => (event: React.ChangeEvent<HTMLSelectElement>) => {
    uberProofDispatch({
      type: 'UPDATE_FIELD',
      payload: { field, value: event.target.value },
    });
  };

  const handleNumberInputChange = (field: keyof DocumentDetail) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseFloat(event.target.value);
    const value = isNaN(parsed) ? null : parsed;
    uberProofDispatch({
      type: 'UPDATE_FIELD',
      payload: { field, value },
    });
  };

  const handleFileChange = (event: FileEvent) => {
    uberProofDispatch({
      type: 'UPDATE_FIELD',
      payload: { field: 'frontFile', value: event.file },
    });
  };

  const handleSubmit = (event: FormEvent) => {
    setWasSubmitted(true);
    event.preventDefault();

    const errors = validateUberProof(uberProofDetails, declarationChecked);
    setValidationErrors(errors);

    if (errors.length === 0) {
      setIsUploading(true);

      // Sanitise details for upload
      const sanitisedDetails = { ...uberProofDetails };
      if (sanitisedDetails.uberTenure === UberTenure.NEW) {
        sanitisedDetails.tripsCompleted = null;
        sanitisedDetails.uberRating = null;
      } else if (sanitisedDetails.uberRating !== null) {
        sanitisedDetails.uberRating = Math.round(sanitisedDetails.uberRating * 100) / 100;
      }

      // Build upload request
      const uploadRequest = CustomerDocumentUploadRequest.fromDocumentDetail('Uber Profile', sanitisedDetails);
      uploadRequest.zohoModule = 'Potentials';
      uploadRequest.zohoModuleUniqueId = potentialId;

      sentryLogger.info(`Preparing Uber Profile upload:`, { uploadRequest });
      DocumentStoreService.prepareCustomerDocumentUpload(
        uploadRequest,
        { msalContext, isAuthenticated },
        country,
        custId,
      )
        .then(preparationResponse => {
          sentryLogger.info(`Uber Profile upload prepared with ID: ${preparationResponse.document_store_record_id}`);

          const uploadUrl = preparationResponse.presigned_upload_urls[0]?.presigned_upload_url;
          if (!uploadUrl) {
            throw new Error('No upload URL received');
          }

          const file = sanitisedDetails.frontFile!;

          return DocumentStoreService.uploadFile(uploadUrl, file, file.type)
            .then(uploadResult => {
              if (!uploadResult.success) {
                console.error('Upload failed:', uploadResult.error);
                setError(true);
                setWasSubmitted(false);
                setIsUploading(false);
              } else {
                sentryLogger.info(`Uber Profile upload for ${preparationResponse.document_store_record_id} completed successfully`);
                navigateToNextStep();
              }
            });
        })
        .catch(error => {
          console.error('Upload preparation failed:', error);
          setError(true);
          setIsUploading(false);
        });
    } else {
      setWasSubmitted(false);
    }
  };

  return (
    <>
      <NavBar pageTitle='Uber Proof' />
      {isUploading &&
        <div
          data-testid="loading-spinner"
          style={{ display: 'flex', justifyContent: 'center', margin: '15vh' }}
        >
          <Elliptical />
        </div>
      }
      {!isUploading &&
        <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={handleSubmit}>
          <div style={{ marginTop: '2vh', fontSize: '20px', fontWeight: 'bold' }}>
            Uber Proof
          </div>

          {validationErrors.length > 0 &&
            <WarningBox
              testId='validation-errors'
              message={
                <>
                  <div>Please fix the following errors before submitting:</div>
                  <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </>
              }
            />
          }

          <div style={{ marginTop: '2vh' }}>
            <Label text='Screenshot' requiredFieldInd />
            <UploadFilePanel
              file={uberProofDetails.frontFile || undefined}
              allowedTypes={FILE_ACCEPT_TYPES}
              required
              testId='screenshot-file'
              onSelect={handleFileChange}
            />
          </div>

          <div style={{ marginTop: '2vh' }}>
            <Label text='Uber Tenure' requiredFieldInd />
            <DropDownMenu
              menuName='uber-tenure'
              testId='uber-tenure-dropdown'
              required
              choices={Object.values(UberTenure).map(choice => ({ value: choice, enabled: true }))}
              onSelect={handleSelectChange('uberTenure')}
            />
          </div>

          {uberProofDetails.uberTenure !== UberTenure.NEW && (
            <div style={{ marginTop: '2vh' }}>
              <Label text='Trips Completed' requiredFieldInd />
              <DropDownMenu
                menuName='trips-completed'
                testId='trips-completed-dropdown'
                required
                choices={Object.values(TripsCompleted).map(choice => ({ value: choice, enabled: true }))}
                onSelect={handleSelectChange('tripsCompleted')}
              />
            </div>
          )}

          {uberProofDetails.uberTenure !== UberTenure.NEW && (
            <div style={{ marginTop: '2vh' }}>
              <Label text='Uber Rating' requiredFieldInd />
              <input
                type='number'
                name='uber-rating'
                data-testid='uber-rating-input'
                min='1'
                max='5'
                step='0.01'
                required
                onChange={handleNumberInputChange('uberRating')}
                onWheel={preventScrollIncrement}
                style={textBoxStyle}
              />
            </div>
          )}

          <div style={{ marginTop: '2vh' }}>
            <Label text='Hours Driven per Week' requiredFieldInd />
            <DropDownMenu
              menuName='driving-hours'
              testId='driving-hours-dropdown'
              required
              choices={Object.values(HoursDrivenPerWeek).map(choice => ({ value: choice, enabled: true }))}
              onSelect={handleSelectChange('drivingHoursPerWeek')}
            />
          </div>

          <div data-testid="declaration-panel" style={{ margin: '2.5vh 0' }}>
            <Checkbox
              id="declaration"
              label={
                `I, ${msalContext.accounts.at(0)?.name}, declare the customer has an Active Uber ` +
                `Rideshare Driver Profile and the trips stated match their profile screenshot.`
              }
              required
              onChange={setDeclarationChecked}
            />
          </div>

          <div style={{ margin: '2.5vh 0' }}>
            <SubmitBtn
              text='Submit'
              testId='submit-button'
              disabled={wasSubmitted}
            />
          </div>

          {
            getEnvTag() === EnvTag.Production ||
            <div style={{ margin: '2.5vh 0' }}>
              <Button
                label="Skip Uber Proof step"
                btnType={ButtonStyle.tertiary}
                onClickFunc={navigateToNextStep}
              />
            </div>
          }
        </form>
      }
      {
        error &&
        <WarningModal
          message='Something went wrong'
          additionalInfo='We&#39;re sorry, we were unable to process your request. Please try again later and if the issue persists, please log a ticket with IT Helpdesk.'
          onClickClose={(event: any) => {
            event.preventDefault();
            setWasSubmitted(false);
            setError(false);
          }} />
      }
    </>
  );
}